import React from "react";
import { type ZudokuConfig, type ZudokuPlugin } from "zudoku";

// Mapeamento de rotas para breadcrumbs e títulos estruturados
const ROUTE_INFO: Record<string, { category: string; title: string }> = {
  "/docs": { category: "Geral", title: "Documentação Geral" },
  "/criar-conta": { category: "Primeiros Passos", title: "Como criar minha conta no Nottou" },
  "/cadastrar-empresa": { category: "Primeiros Passos", title: "Como cadastrar os dados da minha empresa" },
  "/configurar-certificado": { category: "Primeiros Passos", title: "Como configurar o Certificado Digital A1" },
  "/configurar-serie": { category: "Primeiros Passos", title: "Como configurar Série e Ambiente" },
  "/emitir-nfse": { category: "Primeiros Passos", title: "Como emitir minha primeira NFS-e" },
  "/encontrar-servico": { category: "Emissão Avançada", title: "Como preencher o campo de Serviço na NFS-e" },
  "/emitir-massa": { category: "Emissão Avançada", title: "Como emitir várias Notas Fiscais de uma vez" },
  "/importar-planilha": { category: "Emissão Avançada", title: "Como emitir Notas Fiscais importando planilha" },
  "/parametros-nfse": { category: "Emissão Avançada", title: "Como automatizar o preenchimento da NFS-e" },
  "/consultar-notas": { category: "Gestão de Notas", title: "Como consultar e filtrar Notas Fiscais" },
  "/cancelar-nota": { category: "Gestão de Notas", title: "Como cancelar uma Nota Fiscal já emitida" },
  "/auth": { category: "Autenticação", title: "Autenticação via API Key e JWT" },
  "/api": { category: "API Reference", title: "Visão Geral da API Nottou" },
  "/api/nfs-e": { category: "API Reference", title: "Endpoints de Emissão e Consulta de NFS-e" },
  "/api/empresas": { category: "API Reference", title: "Endpoints de Gestão de Empresas" },
  "/api/tomadores": { category: "API Reference", title: "Endpoints de Gestão de Tomadores de Serviço" },
  "/api/servicos": { category: "API Reference", title: "Endpoints de Consulta de Serviços LC 116/03" },
  "/api/series": { category: "API Reference", title: "Endpoints de Gestão de Séries de NFS-e" },
  "/api/enderecos": { category: "API Reference", title: "Endpoints de Consulta de Endereço e CEP" },
  "/api/nbs": { category: "API Reference", title: "Endpoints de Consulta da Tabela NBS" },
};

const seoPlugin: ZudokuPlugin = {
  getHead: ({ location }) => {
    const currentPath = location.pathname.replace(/\/$/, "") || "/";
    const routeData = ROUTE_INFO[currentPath];
    const canonicalUrl = `https://docs.nottou.com.br${currentPath === "/" ? "" : currentPath}`;

    const schemas: object[] = [
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "Nottou Docs",
        url: "https://docs.nottou.com.br",
        description: "Documentação da API Nottou para emissão de Notas Fiscais de Serviço Eletrônicas (NFS-e) no padrão nacional",
        publisher: {
          "@type": "Organization",
          name: "Nottou",
          url: "https://nottou.com.br",
          logo: "https://docs.nottou.com.br/nottou.svg",
        },
      },
    ];

    if (routeData) {
      // BreadcrumbList Schema
      schemas.push({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://docs.nottou.com.br/docs",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: routeData.category,
            item: `https://docs.nottou.com.br${currentPath.startsWith("/api") ? "/api" : "/docs"}`,
          },
          {
            "@type": "ListItem",
            position: 3,
            name: routeData.title,
            item: canonicalUrl,
          },
        ],
      });

      // TechArticle / HowTo Schema para páginas de documentação e guias
      schemas.push({
        "@context": "https://schema.org",
        "@type": "TechArticle",
        headline: routeData.title,
        description: `Guia oficial e documentação técnica sobre ${routeData.title.toLowerCase()} na plataforma Nottou.`,
        url: canonicalUrl,
        inLanguage: "pt-BR",
        author: {
          "@type": "Organization",
          name: "Nottou",
          url: "https://nottou.com.br",
        },
        publisher: {
          "@type": "Organization",
          name: "Nottou",
          logo: {
            "@type": "ImageObject",
            url: "https://docs.nottou.com.br/nottou.svg",
          },
        },
      });
    }

    return (
      <>
        {/* Open Graph Tags */}
        <meta property="og:site_name" content="Nottou Docs" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content="https://docs.nottou.com.br/nottou.svg" />
        <meta property="og:locale" content="pt_BR" />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@nottou" />
        <meta name="twitter:creator" content="@nottou" />
        <meta name="twitter:image" content="https://docs.nottou.com.br/nottou.svg" />

        {/* JSON-LD Structured Data */}
        {schemas.map((schema, index) => (
          <script key={index} type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        ))}
      </>
    );
  },
};

const config: ZudokuConfig = {
  canonicalUrlOrigin: "https://docs.nottou.com.br",
  sitemap: {
    siteUrl: "https://docs.nottou.com.br",
    changefreq: "weekly",
    priority: 0.8,
  },
  site: {
    logo: {
      src: {
        light: "/nottou.svg",
        dark: "/nottou.svg",
      },
      width: "120px",
    },
  },
  navigation: [
    { type: "category", label: "Primeiros Passos", items: ["criar-conta", "cadastrar-empresa", "configurar-certificado", "configurar-serie", "emitir-nfse"] },
    { type: "category", label: "Emissão Avançada", items: ["encontrar-servico", "emitir-massa", "importar-planilha", "parametros-nfse"] },
    { type: "category", label: "Gestão de Notas", items: ["consultar-notas", "cancelar-nota"] },
    { type: "category", label: "Autenticação", items: ["auth"] },
    { type: "link", to: "api", label: "API Reference" },
  ],
  metadata: {
    favicon: "/favicon.ico",
    title: "%s | Nottou Docs",
    defaultTitle: "Nottou Docs - API NFS-e Nacional",
    description: "Documentação completa da API Nottou para emissão de Notas Fiscais de Serviço Eletrônicas (NFS-e) no padrão nacional.",
    keywords: ["NFS-e", "API", "Nota Fiscal", "Eletrônica", "Serviço", "NFS-e Nacional", "Zudoku", "Nottou"],
    robots: "index, follow",
  },
  redirects: [{ from: "/", to: "/docs" }],
  apis: {
    type: "file",
    input: "./open-api.json",
    path: "/api",
  },
  docs: {
    files: "/pages/**/*.{md,mdx}",
  },
  search: {
    type: "pagefind",
    maxSubResults: 3,
  },
  plugins: [seoPlugin],
  theme: {
    dark: {
      primary: "#0019ff",
      primaryForeground: "#ffffff",
    },
    light: {
      primary: "#0019ff",
      primaryForeground: "#ffffff",
    },
  },
};

export default config;
